import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import MyBestie from './pages/MyBestie';
import Tasks from './pages/Tasks';
import Recovery from './pages/Recovery';
import Burnout from './pages/Burnout';
import BurnoutWatch from './pages/BurnoutWatch';
import Buddy from './pages/Buddy';
import BuddyChat from './pages/BuddyChat';
import Donations from './pages/Donations';
import CompanyDashboard from './pages/CompanyDashboard';
import Profile from './pages/Profile';
import ReminderSettingsPage from './pages/ReminderSettingsPage';
import { ReminderProvider } from './context/ReminderContext';
import { ThemeProvider } from './context/ThemeContext';
import ReminderContainer from './components/ReminderContainer';
import ThemeToggle from './components/ThemeToggle';

// Super Admin imports
import AdminLogin from './pages/admin/AdminLogin';
import AdminForgotPassword from './pages/admin/AdminForgotPassword';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import CompanyManagement from './pages/admin/CompanyManagement';
import DonationManagement from './pages/admin/DonationManagement';
import AuditLogs from './pages/admin/AuditLogs';
import AdminProfile from './pages/admin/AdminProfile';

//buddy
import FacilitatorDashboard from './pages/FacilitatorDashboard';
import AdminFacilitatorsPage from './pages/admin/AdminFacilitatorsPage';   

console.log('App.js loading');

function App() {
  console.log('App component rendering...');
  
  // Use state instead of direct localStorage check so component re-renders
  const [isAuthenticated, setIsAuthenticated] = React.useState(!!localStorage.getItem('access_token'));
  const [isAdminAuthenticated, setIsAdminAuthenticated] = React.useState(!!localStorage.getItem('admin_access_token'));
  
  // Listen for storage changes (for multi-tab support and manual triggers)
  React.useEffect(() => {
    const handleStorageChange = () => {
      setIsAuthenticated(!!localStorage.getItem('access_token'));
      setIsAdminAuthenticated(!!localStorage.getItem('admin_access_token'));
    };
    
    // Listen to storage events from other tabs
    window.addEventListener('storage', handleStorageChange);
    
    // Listen to custom event for same-tab changes
    window.addEventListener('authChange', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChange', handleStorageChange);
    };
  }, []);
  
  return (
    <ThemeProvider>
      <ReminderProvider>
        <Router>
          <ReminderContainer>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={!isAuthenticated ? <Home /> : <Navigate to="/dashboard" />} />
              <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />} />
              <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/dashboard" />} />
              <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
              
              {/* Protected routes */}
              <Route path="/dashboard" element={isAuthenticated ? <Dashboard /> : <Navigate to="/login" />} />
              <Route path="/my-bestie" element={isAuthenticated ? <MyBestie /> : <Navigate to="/login" />} />
              <Route path="/tasks" element={isAuthenticated ? <Tasks /> : <Navigate to="/login" />} />
              <Route path="/recovery" element={isAuthenticated ? <Recovery /> : <Navigate to="/login" />} />
              <Route path="/burnout" element={isAuthenticated ? <Burnout /> : <Navigate to="/login" />} />
              <Route path="/burnout-watch" element={isAuthenticated ? <BurnoutWatch /> : <Navigate to="/login" />} />
              <Route path="/buddy" element={isAuthenticated ? <Buddy /> : <Navigate to="/login" />} />
              <Route path="/buddy/chat/:matchId" element={isAuthenticated ? <BuddyChat /> : <Navigate to="/login" />} />
              <Route path="/donations" element={isAuthenticated ? <Donations /> : <Navigate to="/login" />} />
              <Route path="/company" element={isAuthenticated ? <CompanyDashboard /> : <Navigate to="/login" />} />
              <Route path="/profile" element={isAuthenticated ? <Profile /> : <Navigate to="/login" />} />
              <Route path="/settings/reminders" element={isAuthenticated ? <ReminderSettingsPage /> : <Navigate to="/login" />} />

              {/* buddy */}
              <Route path="/facilitator/dashboard" element={<FacilitatorDashboard />} /> 
              
              {/* Super Admin routes */}
              <Route path="/admin/login" element={!isAdminAuthenticated ? <AdminLogin /> : <Navigate to="/admin/dashboard" />} />
              <Route path="/admin/forgot-password" element={!isAdminAuthenticated ? <AdminForgotPassword /> : <Navigate to="/admin/dashboard" />} />
              <Route path="/admin" element={isAdminAuthenticated ? <AdminLayout /> : <Navigate to="/admin/login" />}>
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="companies" element={<CompanyManagement />} />
                <Route path="donations" element={<DonationManagement />} />
                <Route path="facilitators" element={<AdminFacilitatorsPage />} />
                <Route path="audit-logs" element={<AuditLogs />} />
                <Route path="profile" element={<AdminProfile />} />
              </Route>
              
              {/* Default route */}
              <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} />} />
            </Routes>
            
            {/* Theme Toggle - Show on all authenticated pages */}
            {isAuthenticated && <ThemeToggle />}
          </ReminderContainer>
        </Router>
      </ReminderProvider>
    </ThemeProvider>
  );
}

export default App;
